import React from 'react';
import styled from 'styled-components';

export type InferredInputType = 'boolean' | 'url' | 'number' | 'text';

interface SmartValueInputProps {
  inputType: InferredInputType;
  value: string;
  onChange: (next: string) => void;
  hasError?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  direction: ltr;
  text-align: right;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }

  &:disabled {
    background: var(--background-secondary);
    cursor: not-allowed;
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  padding: 4px 0;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--primary-color);
`;

const StateText = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

/**
 * הסקת סוג השדה לפי הערך עצמו, לפי הכללים:
 *   "0" / "1"          → boolean (checkbox)
 *   "https://..."      → url
 *   ערך מספרי תקני      → number
 *   אחרת               → text
 */
export function inferInputType(value: string | null | undefined): InferredInputType {
  const v = value ?? '';
  if (v === '0' || v === '1') return 'boolean';
  if (v.startsWith('https://') || v.startsWith('http://')) return 'url';
  if (v.trim() !== '' && !isNaN(Number(v))) return 'number';
  return 'text';
}

const SmartValueInput: React.FC<SmartValueInputProps> = ({
  inputType,
  value,
  onChange,
  hasError,
  placeholder,
  disabled
}) => {
  if (inputType === 'boolean') {
    const isChecked = value === '1';
    return (
      <CheckboxRow>
        <Checkbox
          type="checkbox"
          checked={isChecked}
          onChange={(e) => onChange(e.target.checked ? '1' : '0')}
          disabled={disabled}
        />
        <StateText>{isChecked ? 'מופעל (1)' : 'כבוי (0)'}</StateText>
      </CheckboxRow>
    );
  }

  if (inputType === 'url') {
    return (
      <Input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        hasError={hasError}
        placeholder={placeholder ?? 'https://...'}
        disabled={disabled}
      />
    );
  }

  if (inputType === 'number') {
    const hasDecimal = value.includes('.');
    return (
      <Input
        type="number"
        step={hasDecimal ? '0.01' : '1'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        hasError={hasError}
        placeholder={placeholder ?? '0'}
        disabled={disabled}
      />
    );
  }

  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      hasError={hasError}
      placeholder={placeholder ?? 'הזן ערך'}
      disabled={disabled}
    />
  );
};

export default SmartValueInput;
